import * as React from 'react';
import {useEffect, useState} from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {Accordion, AccordionDetails, AccordionSummary, Autocomplete, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid2 as Grid, InputLabel, Link, MenuItem, Select, TextField, Tooltip} from '@mui/material';
import {Add, ArrowDropUp, AttachMoney, Delete, FilterAltOff, KeyboardArrowDown, MoneyOff} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {useTitle} from '../dashboard/TitleContext';
import {useConfirmationDialog} from '../../util/ConfirmationDialogProvider';
import simpleInvoiceService from '../../services/SimpleInvoiceService';
import {parseToMoney} from '../../util/StringUtils';
import authService from '../../services/AuthService';
import axios from 'axios';
import {API_URL} from '../../App';
import {saveAs} from 'file-saver';
import {BarChart} from '@mui/x-charts/BarChart';
import userService from '../../services/UserService';
import simpleInvoicePaymentPartService from '../../services/SimpleInvoicePaymentPartService';
import {pieArcLabelClasses, PieChart} from '@mui/x-charts';
import projectService from '../../services/ProjectService';
import vehicleService from '../../services/VehicleService';
import {ArrowDropDownIcon} from "@mui/x-date-pickers";
import IconButton from "@mui/material/IconButton";
import {useSnackbar} from "../../util/SnackbarProvider";
import FilterPanel from "../common/FilterPanel";

const ALL_COLUMNS = [
        {id: 'serialNumber', label: 'Azonosító'},
        {id: 'name', label: 'Név'},
        {id: 'projectName', label: 'Projekt'},
        {id: 'vehicleLicensePlate', label: 'Jármű'},
        {id: 'paid', label: 'Státusz', fun: p => p === true ? "Teljesített" : "Teljesítetlen"},
        {id: 'sender', label: 'Feladó'},
        {id: 'receiver', label: 'Címzett'},
        {id: 'amount', label: 'Összeg', fun: (a, invoice) => !invoice.paid && invoice.totalPaymentParts ? parseToMoney(a - invoice.totalPaymentParts) + ' (' + parseToMoney(a) + ')' : parseToMoney(a)},
        {id: 'dateOfInvoice', label: 'Keltezés'},
        {id: 'deadline', label: 'Határidő'},
        {id: 'transactionType', label: 'Tranzakció Típusa'},
        {id: 'category', label: 'Kategória'},
        {id: 'paymentType', label: 'Fizetés Módja'},
        {id: 'invoiceType', label: 'Számla Típusa'},
        {id: 'paidByUserName', label: 'Teljesítette', align: "center"},
        {id: 'paidAt', label: 'Teljesítés Dátuma', align: "center"},
        {id: 'documentNames', label: 'Dokumentumok'},
        {id: 'createdAt', label: 'Létrehozva'},
        {id: 'updatedAt', label: 'Módosítva'},
    ]
;

const invoiceTypes = [
    {id: "INVOICE", name: "Számla"},
    {id: "ADVANCE_INVOICE", name: "Előlegszámla"},
    {id: "FEE_REQUEST", name: "Díjbekérő"},
    {id: "RECEIPT", name: "Nyugta"}
]

const paymentTypes = [
    {id: "CASH", name: "Készpénz"},
    {id: "TRANSFER", name: "Átutalás"},
    {id: "CREDIT_CARD", name: "Bankkártya"},
    {id: "CASH_ON_DELIVERY", name: "Utánvét"}
]

const transactionTypes = [
    {id: "EXPENSE", name: "Kiadás"},
    {id: "INCOME", name: "Bevétel"}
]

const categories = [
    {id: "GENERAL", name: "Általános"},
    {id: "SALARY_PAYMENT", name: "Bérkifizetés"},
    {id: "VEHICLE_PAYMENT", name: "Jármű"},
    {id: "TAX_PAYMENT", name: "Adó"},
    {id: "OPERATIONAL_COST", name: "Üzemeltetés"},
]

const DEFAULT_COLUMNS = ['serialNumber', 'name', 'sender', 'receiver', 'amount', 'updatedAt', 'documentNames'];
const LOCAL_STORAGE_ITEM_NAME = "simple-invoices-rows-per-page"
const LOCAL_STORAGE_SEARCH_PARAMS = "simple-invoices-search-params"
const DEFAULT_SEARCH_PARAMS = {orderBy: 'updated_at_desc'}
const LOCAL_STORAGE_COLUMN_NAMES = "simple-invoices-column-names"
const TODAY = new Date().toISOString().split('T')[0];

export default function SimpleInvoiceTable() {

    useTitle([{name: 'Számlák'}])

    const [tableData, setTableData] = useState({
        page: 0,
        rowsPerPage: localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) || 25,
        totalCount: 0
    })

    const {openConfirmationDialog} = useConfirmationDialog()
    const {sendAlertMessage} = useSnackbar()
    const [expanded, setExpanded] = useState(true);
    const [searchParams, setSearchParams] = useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_SEARCH_PARAMS)) || DEFAULT_SEARCH_PARAMS)
    const [columnNames, setColumnNames] = React.useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMN_NAMES)) || DEFAULT_COLUMNS)
    const [stats, setStats] = useState()
    const [expenseChartData, setExpenseChartData] = useState()
    const filterPanelRef = React.useRef();

    useEffect(() => {
        retrieveSimpleInvoices()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData.page, tableData.rowsPerPage, searchParams]);

    const retrieveSimpleInvoices = () => {
        simpleInvoiceService.findAll(buildQueryParams())
            .then(response => {
                const result = response.simpleInvoices.content.map(invoice => ({
                    ...invoice,
                    invoiceType: invoiceTypes.find(s => s.id === invoice.invoiceType)?.name,
                    paymentType: paymentTypes.find(s => s.id === invoice.paymentType)?.name,
                    transactionType: transactionTypes.find(s => s.id === invoice.transactionType)?.name,
                    category: categories.find(s => s.id === invoice.category)?.name
                }));
                setTableData((prev) => ({...prev, totalCount: response.simpleInvoices.totalElements, rows: result}))
                setStats(response.stats)
            })
            .catch((error) => {
                sendAlertMessage(error?.message || 'Számlák lekérése sikertelen!', 'error')
                setTableData(prev => ({...prev, rows: []}))
            })
    }

    useEffect(() => {
        if (!stats) return
        const columnCount = Object.values(stats.monthlyCosts)[0]?.length || 0;
        const columnSums = Array(columnCount).fill(0);
        Object.values(stats.monthlyCosts).forEach((row) => {
            row.forEach((value, index) => {
                columnSums[index] += value;
            });
        });
        setExpenseChartData(columnSums)
    }, [stats]);


    const [users, setUsers] = React.useState([]);
    useEffect(() => {
        userService.getSelectors()
            .then(response => setUsers(response))
            .catch(error => console.error(error))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [projects, setProjects] = useState([]);
    useEffect(() => {
        projectService.getSelectors()
            .then(response => setProjects(response))
            .catch(error => console.error(error))
    }, []);

    const [vehicles, setVehicles] = useState([]);
    useEffect(() => {
        vehicleService.getSelectors()
            .then(response => setVehicles(response))
            .catch(error => console.error(error))
    }, []);

    function buildQueryParams() {
        const copy = structuredClone(searchParams)
        if (copy.vehicle?.id) copy.vehicleId = copy.vehicle.id
        if (copy.project?.id) copy.projectId = copy.project.id
        delete copy?.vehicle
        delete copy?.project
        return {
            page: tableData.page,
            size: tableData.rowsPerPage,
            ...Object.fromEntries(
                Object.entries(copy).filter(([_, value]) => value !== null)
            )
        };
    }

    const deleteSimpleInvoice = (id) => {
        simpleInvoiceService.deleteSimpleInvoice(id)
            .then(() => {
                retrieveSimpleInvoices()
                sendAlertMessage('Számla sikeresen törölve!')
            })
            .catch(() => sendAlertMessage('Számla törlése sikertelen!', 'error'))
    }

    const togglePaid = (id, paid, paidAt, userId) => {
        simpleInvoiceService.togglePaid(id, userId ? {paidAt: paidAt, userId: userId} : null)
            .then(() => {
                sendAlertMessage(paid ? 'Számla teljesítése visszavonva' : 'Számla teljesítve')
                retrieveSimpleInvoices()
            })
            .catch(() => sendAlertMessage('Számla teljesítése sikertelen!', 'error'))
    }

    const saveAsPaymentPart = (id, formJson) => {
        simpleInvoicePaymentPartService.createPaymentPart(id, {
            paidAt: formJson.paidAt,
            userId: formJson.userId,
            amount: formJson.amount
        })
            .then(() => {
                retrieveSimpleInvoices()
                sendAlertMessage('Számla részleges teljesítése sikeres!')
            })
            .catch(error => sendAlertMessage(error?.message || "Számla részleges teljesítése sikertelen", 'error'))
    }

    const navigate = useNavigate();

    const onSearchParamsChange = (event) => {
        const {name, value} = event.target
        setSearchParams((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const onChangeCheckbox = (event) => {
        const {checked, name} = event.target;
        setSearchParams((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const onChangeSelect = (event, newValue, name) => {
        setSearchParams((prev) => ({
            ...prev,
            [name]: newValue,
        }));
    };

    const clear = () => {
        if (searchParams !== DEFAULT_SEARCH_PARAMS) sendAlertMessage('Szűrők alaphelyzetbe állítva', 'info')
        setSearchParams(DEFAULT_SEARCH_PARAMS)
        filterPanelRef.current?.resetSelection();
    }

    useEffect(() => {
        if (searchParams) localStorage.setItem(LOCAL_STORAGE_SEARCH_PARAMS, JSON.stringify(searchParams));
        else localStorage.removeItem(LOCAL_STORAGE_SEARCH_PARAMS)
        setTableData((prev) => ({...prev, page: 0}))
    }, [searchParams]);

    const renderDocuments = (column, value, row) => {
        return (
            <Link onClick={(e) => {
                e.stopPropagation()
                downloadFile(row.id, value[0])
            }}
                  sx={{textDecoration: "none", cursor: "pointer", fontWeight: "bold"}}>
                {value[0]}
            </Link>)
    }

    const downloadFile = async (id, fileName) => {
        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        axios({
            url: API_URL + "/simple-invoices/" + id + "/document",
            params: {fileName: fileName},
            method: 'GET',
            responseType: 'blob',
            headers: headers,
        })
            .then(response => {
                const contentType = response.headers['content-type'];
                const blob = new Blob([response.data], {type: contentType});
                const url = window.URL.createObjectURL(blob);

                if (contentType === 'application/pdf' || contentType.startsWith('image/')) {
                    window.open(url)
                } else {
                    saveAs(blob, fileName);
                    sendAlertMessage('Fájl sikeresen letöltve')
                }
            })
            .catch(error => {
                console.error(error)
                sendAlertMessage('Fájl letöltése sikertelen', 'error')
            });
    }

    function valueFormatter(value) {
        return `${value?.toLocaleString()} Ft`;
    }

    function valueFormatterWithPercentage(value, data) {
        const entry = Object.entries(stats.monthlyCosts)?.[data.dataIndex]?.[1]
        if (entry === undefined) return
        const totalExpense = entry.slice(0, 6).reduce((acc, curr) => acc + curr, 0);
        const percentage = totalExpense ? ((value / totalExpense) * 100).toFixed(1) : 0;
        return `${value.toLocaleString()} Ft (${percentage}%)`;
    }

    const [open, setOpen] = React.useState(false);
    const [currentItem, setCurrentItem] = React.useState(undefined);

    const handleClickOpen = (row) => {
        setCurrentItem(row)
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const loggedInUser = JSON.parse(localStorage.getItem('user'))
    const [selectedUser, setSelectedUser] = useState({id: loggedInUser.userId, name: loggedInUser.username});

    const calculateTotal = (data, start, end) => data ? data.slice(start, end).reduce((acc, curr) => acc + curr, 0) : 0;

    const prepareExpenseChartData = () => {
        const expense = calculateTotal(expenseChartData, 0, 6);
        const income = calculateTotal(expenseChartData, 6, 8);
        const max = Math.max(expense, income, 1);

        return [
            {
                id: 0,
                value: expenseChartData[0],
                label: `Átalános (${(expenseChartData[0] * 100 / max).toFixed(1)}%)`,
                color: '#cc0000'
            },
            {
                id: 2,
                value: expenseChartData[1],
                label: `Bérkifizetés (${(expenseChartData[1] * 100 / max).toFixed(1)}%)`,
                color: '#ff7f00'
            },
            {
                id: 3,
                value: expenseChartData[2],
                label: `Járművek (${(expenseChartData[2] * 100 / max).toFixed(1)}%)`,
                color: '#ffd966'
            },
            {
                id: 1,
                value: expenseChartData[3],
                label: `Adó (${(expenseChartData[3] * 100 / max).toFixed(1)}%)`,
                color: '#ff0000'
            },
            {
                id: 4,
                value: expenseChartData[4],
                label: `Üzemeltetési Költség (${(expenseChartData[4] * 100 / max).toFixed(1)}%)`,
                color: '#E0B0FF'
            },
            {
                id: 5,
                value: expenseChartData[5],
                label: `Teljesítetlen Kiadás (${(expenseChartData[5] * 100 / max).toFixed(1)}%)`,
                color: '#efb2b2'
            },
            {
                id: 6,
                value: expenseChartData[6],
                label: `Teljesített Bevétel (${(expenseChartData[6] * 100 / max).toFixed(1)}%)`,
                color: 'green'
            },
            {
                id: 7,
                value: expenseChartData[7],
                label: `Teljesítetlen Bevétel (${(expenseChartData[7] * 100 / max).toFixed(1)}%)`,
                color: '#b2d8b2'
            },
        ];
    };

    const calculateStartAngle = () => {
        const totalExpenses = calculateTotal(expenseChartData, 0, 6);
        const totalRevenues = calculateTotal(expenseChartData, 6, 8);
        const max = Math.max(totalExpenses, totalRevenues)
        return Math.abs(1 - (totalExpenses + totalRevenues) / (2 * max)) * 100 * 3.6;
    };

    const sortButton = (name) => {
        return (
            <IconButton onClick={() => setSearchParams(prev => ({
                ...prev,
                orderBy: name,
                sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'
            }))}>
                {searchParams.sort == null || searchParams.orderBy !== name ?
                    <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                    searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
            </IconButton>)
    }

    const onChangeColumnCheckBox = (event) => {
        const {checked, name} = event.target;
        setColumnNames((prev) => {
            return checked ? prev.includes(name) ? prev : [...prev, name] : prev.filter((col) => col !== name);
        });
    };

    useEffect(() => {
        if (columnNames) localStorage.setItem(LOCAL_STORAGE_COLUMN_NAMES, JSON.stringify(columnNames));
        else localStorage.removeItem(LOCAL_STORAGE_COLUMN_NAMES)
    }, [columnNames]);

    return (
        <>
            <Accordion style={{marginBottom: "10px"}} disableGutters expanded={expanded}
                       onChange={() => setExpanded(!expanded)}>
                <AccordionSummary
                    expandIcon={<KeyboardArrowDown sx={{marginRight: "0px", height: "2em", width: "auto"}}/>} sx={{
                    minHeight: "0px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    backgroundColor: "darkgray",
                    borderRadius: "4px 4px 0px 0px",
                    paddingLeft: "20px",
                    margin: "0"
                }}>
                    Keresési Feltételek
                </AccordionSummary>
                <AccordionDetails className="filter-panel">
                    <Grid container columns={10} spacing={2} padding={"10px"}>
                        <Grid size={{xs: 7}}>
                            <Grid container columns={20} spacing={2} padding={"10px"}>

                                <Grid size={{xl: 5, lg: 5, md: 10, xs: 10}}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        type="date"
                                        slotProps={{inputLabel: {shrink: true}}}
                                        label="-tól"
                                        name="fromDate"
                                        autoComplete="off"
                                        onChange={onSearchParamsChange}
                                        sx={{marginTop: "0px"}}
                                        value={(searchParams && searchParams.fromDate) || "-1"}
                                    />
                                </Grid>

                                <Grid size={{xl: 5, lg: 5, md: 10, xs: 10}}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        type="date"
                                        slotProps={{inputLabel: {shrink: true}}}
                                        label="-ig"
                                        name="toDate"
                                        autoComplete="off"
                                        onChange={onSearchParamsChange}
                                        sx={{marginTop: "0px"}}
                                        value={(searchParams && searchParams.toDate) || "-1"}
                                    />
                                </Grid>

                                <Grid size={{xl: 5, lg: 5, md: 10, xs: 10}}>
                                    <FormControlLabel sx={{marginTop: "0px"}} control={<Checkbox
                                        margin="normal"
                                        size="large"
                                        onChange={onChangeCheckbox}
                                        name="onlyExpired"
                                        checked={(searchParams && searchParams.onlyExpired) || false}
                                    />} label={<span style={{fontSize: "16px"}}>Csak lejártak</span>}/>
                                </Grid>

                                <Grid size={{xl: 5, lg: 5, md: 10, xs: 10}}>
                                    <FormControlLabel sx={{marginTop: "0px"}} control={<Checkbox
                                        margin="normal"
                                        size="large"
                                        onChange={onChangeCheckbox}
                                        name="onlyPartlyPaid"
                                        checked={(searchParams && searchParams.onlyPartlyPaid) || false}
                                    />} label={<span style={{fontSize: "16px"}}>Csak részlegesen teljesítettek</span>}/>
                                </Grid>

                                <Grid size={{xs: 20}}><Divider/></Grid>

                                <Grid size={{xs: 20}}>
                                    {ALL_COLUMNS.map((item, index) => (
                                        <FormControlLabel key={index}
                                                          control={<Checkbox
                                                              variant="outlined"
                                                              margin="normal"
                                                              size="large"
                                                              name={item.id}
                                                              onChange={onChangeColumnCheckBox}
                                                              checked={columnNames?.includes(item.id) || false}
                                                              sx={{padding: "0px"}}
                                                          />} label={item.label?.props?.title || item.label}
                                                          style={{paddingRight: "20px", margin: "0px"}}/>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <FilterPanel
                                type="SIMPLE_INVOICE"
                                ref={filterPanelRef}
                                currentSearchParams={searchParams}
                                onFilterSelect={(params) => setSearchParams(params)}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
            <Paper style={{margin: "10px 0px", padding: "16px 0px"}}>
                {stats != null &&
                    <BarChart
                        dataset={Object.entries(stats.monthlyCosts).map(([key, value]) => {
                            return {
                                month: key.slice(0, 7),
                                generalExpense: value[0],
                                salaryPaymentExpense: value[1],
                                vehicleExpense: value[2],
                                taxPaymentExpense: value[3],
                                operationalCostExpense: value[4],
                                totalUnpaidExpense: value[5],
                                totalPaidIncome: value[6],
                                totalUnpaidIncome: value[7]

                            }
                        })}
                        series={[
                            {
                                dataKey: "generalExpense",
                                label: "Általános",
                                color: "#cc0000",
                                stack: 'expense',
                                valueFormatter: valueFormatterWithPercentage
                            },
                            {
                                dataKey: "taxPaymentExpense",
                                label: "Adó",
                                color: "#ff0000",
                                stack: 'expense',
                                valueFormatter: valueFormatterWithPercentage
                            },
                            {
                                dataKey: "salaryPaymentExpense",
                                label: "Bérkifizetés",
                                color: "#ff7f00",
                                stack: 'expense',
                                valueFormatter: valueFormatterWithPercentage
                            },
                            {
                                dataKey: "vehicleExpense",
                                label: "Járművek",
                                color: "#ffd966",
                                stack: 'expense',
                                valueFormatter: valueFormatterWithPercentage
                            },
                            {
                                dataKey: "operationalCostExpense",
                                label: "Üzemeltetési Költség",
                                color: "#E0B0FF",
                                stack: 'expense',
                                valueFormatter: valueFormatterWithPercentage
                            },
                            {
                                dataKey: "totalUnpaidExpense",
                                label: "Teljesítetlen Kiadás",
                                color: "#efb2b2",
                                stack: 'expense',
                                valueFormatter: valueFormatterWithPercentage
                            },
                            {
                                dataKey: "totalPaidIncome",
                                label: "Teljesített Bevétel",
                                color: "green",
                                stack: 'income',
                                valueFormatter
                            },
                            {
                                dataKey: "totalUnpaidIncome",
                                label: "Teljesítetlen Bevétel",
                                color: "#b2d8b2",
                                stack: 'income',
                                valueFormatter
                            }
                        ]}
                        xAxis={[{scaleType: 'band', dataKey: 'month'}]}
                        yAxis={[{position: "left", valueFormatter: (value) => value.toLocaleString() + ' Ft'}]}
                        margin={{top: 50, bottom: 30, left: 110, right: 10}}
                        height={200}
                    />}

            </Paper>
            <Grid container columns={10}>
                {expenseChartData?.length > 0 &&
                    <>
                        <Grid size={{lg: 5, xs: 10}}>
                            <PieChart
                                series={[
                                    {
                                        data: prepareExpenseChartData(),
                                        innerRadius: 20,
                                        outerRadius: 150,
                                        cx: 150,
                                        paddingAngle: 1,
                                        cornerRadius: 3,
                                        arcLabel: (item) => item.label.substring(item.label.indexOf('(') + 1, item.label.indexOf(')')),
                                        arcLabelMinAngle: 30,
                                        highlightScope: {fade: 'global', highlight: 'item'},
                                        faded: {innerRadius: 30, additionalRadius: -30, color: 'gray'},
                                        arcLabelRadius: 80,
                                        startAngle: calculateStartAngle()
                                    },
                                ]}
                                sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                        fontWeight: 'bold',
                                        fontFamily: 'Century Gothic',
                                        fontSize: "16px",
                                        fill: 'white',
                                        shapeRendering: 'geometricPrecision'
                                    }
                                }}
                                height={320}
                            />
                        </Grid>
                        <Grid size={{lg: 5, xs: 10}}>
                            <PieChart
                                series={[
                                    {
                                        data: [
                                            {
                                                id: 0,
                                                value: expenseChartData[0],
                                                label: 'Általános (' + parseToMoney(expenseChartData[0]) + ')',
                                                color: '#cc0000'
                                            },
                                            {
                                                id: 2,
                                                value: expenseChartData[1],
                                                label: 'Bérkifizetés (' + parseToMoney(expenseChartData[1]) + ')',
                                                color: '#ff7f00'
                                            },
                                            {
                                                id: 3,
                                                value: expenseChartData[2],
                                                label: 'Járművek (' + parseToMoney(expenseChartData[2]) + ')',
                                                color: '#ffd966'
                                            },
                                            {
                                                id: 1,
                                                value: expenseChartData[3],
                                                label: 'Adó (' + parseToMoney(expenseChartData[3]) + ')',
                                                color: '#ff0000'
                                            },
                                            {
                                                id: 4,
                                                value: expenseChartData[4],
                                                label: 'Üzemeltetési Költség (' + parseToMoney(expenseChartData[4]) + ')',
                                                color: '#E0B0FF'
                                            },
                                            {
                                                id: 5,
                                                value: expenseChartData[5],
                                                label: 'Teljesítetlen Kiadás (' + parseToMoney(expenseChartData[5]) + ')',
                                                color: '#efb2b2'
                                            }
                                        ],
                                        innerRadius: 20,
                                        outerRadius: 150,
                                        cx: 150,
                                        paddingAngle: 1,
                                        cornerRadius: 3,
                                        arcLabel: (item) => (item.value / expenseChartData.slice(0, 6).reduce((acc, curr) => acc + curr, 0) * 100).toFixed(1) + '%',
                                        arcLabelMinAngle: 30,
                                        highlightScope: {fade: 'global', highlight: 'item'},
                                        faded: {innerRadius: 30, additionalRadius: -30, color: 'gray'},
                                        arcLabelRadius: 80
                                    },
                                ]}
                                sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                        fontWeight: 'bold',
                                        fontFamily: 'Century Gothic',
                                        fontSize: "16px",
                                        fill: 'white',
                                        shapeRendering: 'geometricPrecision'
                                    }
                                }}
                                height={320}
                            /></Grid>
                    </>}
            </Grid>
            <Paper sx={{margin: "10px 0px", width: '100%', overflow: 'hidden'}}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{background: "lightgray", width: "100px"}}></TableCell>
                                <TableCell style={{
                                    fontWeight: 700,
                                    background: "lightgray",
                                    textAlign: "center"
                                }}>Teljesített</TableCell>
                                <TableCell style={{fontWeight: 700, background: "lightgray", textAlign: "center"}}>Teljesítésre
                                    Vár</TableCell>
                                <TableCell style={{
                                    fontWeight: 700,
                                    background: "lightgray",
                                    textAlign: "center"
                                }}>Összesen</TableCell>
                                <TableCell style={{
                                    fontWeight: 700,
                                    background: "lightgray",
                                    textAlign: "center"
                                }}>Lejárt</TableCell>
                                <TableCell style={{fontWeight: 700, background: "lightgray", textAlign: "center"}}>
                                    <Tooltip
                                        title='(Bevétel ÁFA mennyisége - Kiadás ÁFA mennyisége) Negatív szám esetén nem tartozás, hanem túltörlesztés van'><>Fizetendő
                                        ÁFA
                                    </>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{fontWeight: 700, background: "lightgray"}}>Bevétel</TableCell>
                                <TableCell
                                    style={{textAlign: "center"}}>{parseToMoney(stats?.totalPaidIncome, true)}</TableCell>
                                <TableCell
                                    style={{textAlign: "center"}}>{parseToMoney(stats?.totalUnpaidIncome, true)}</TableCell>
                                <TableCell
                                    style={{textAlign: "center"}}>{parseToMoney(stats?.totalPaidIncome + stats?.totalUnpaidIncome, true)}</TableCell>
                                <TableCell style={{
                                    textAlign: "center",
                                    backgroundColor: "#ffffb2"
                                }}>{parseToMoney(stats?.totalExpiredIncome, true)}</TableCell>
                                <TableCell style={{textAlign: "center", backgroundColor: "#ffffb2"}}
                                           rowSpan={2}>{parseToMoney(stats?.totalVat, true)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell style={{fontWeight: 700, background: "lightgray"}}>Kiadás</TableCell>
                                <TableCell
                                    style={{textAlign: "center"}}>{parseToMoney(stats?.totalPaidExpense, true)}</TableCell>
                                <TableCell
                                    style={{textAlign: "center"}}>{parseToMoney(stats?.totalUnpaidExpense, true)}</TableCell>
                                <TableCell
                                    style={{textAlign: "center"}}>{parseToMoney(stats?.totalPaidExpense + stats?.totalUnpaidExpense, true)}</TableCell>
                                <TableCell style={{
                                    textAlign: "center",
                                    backgroundColor: "#ffffb2"
                                }}>{parseToMoney(stats?.totalExpiredExpense, true)}</TableCell>
                            </TableRow>
                            <TableRow sx={{border: "2px solid black"}}>
                                <TableCell style={{fontWeight: 700, background: "lightgray"}}>Összesen</TableCell>
                                <TableCell style={{
                                    textAlign: "center",
                                    background: "#b2d8b2",
                                    fontWeight: 700
                                }}>{parseToMoney(stats?.totalPaidIncome - stats?.totalPaidExpense, true)}</TableCell>
                                <TableCell style={{
                                    textAlign: "center",
                                    background: "#b2d8b2",
                                    fontWeight: 700
                                }}>{parseToMoney(stats?.totalUnpaidIncome - stats?.totalUnpaidExpense, true)}</TableCell>
                                <TableCell style={{
                                    textAlign: "center",
                                    background: "#b2d8b2",
                                    fontWeight: 700
                                }}>{parseToMoney((stats?.totalPaidIncome + stats?.totalUnpaidIncome) - (stats?.totalPaidExpense + stats?.totalUnpaidExpense), true)}</TableCell>
                                <TableCell style={{textAlign: "center", background: "lightgray"}}
                                           colSpan={2}></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper sx={{width: '100%', overflow: 'hidden'}}>
                <TableContainer>
                    <Table stickyHeader aria-label="sticky table" sx={{tableLayout: "fixed"}}>
                        <TableHead>
                            <TableRow>
                                <TableCell key="actions"
                                           style={{width: "100px", paddingLeft: "10px"}}
                                           align='left' padding='none'>
                                    <Tooltip title="Új hozzáadása">
                                        <Button variant='contained' color='success'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={() => navigate("/dashboard/simple-invoices/create")}>
                                            <Add fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Szűrők törlése">
                                        <Button variant='contained' color='info'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={clear}>
                                            <FilterAltOff fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                {columnNames.includes('serialNumber') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <TextField name="serialNumber" label="Azonosító" size="small"
                                                   value={searchParams.serialNumber || ""} onChange={onSearchParamsChange}
                                                   sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>
                                        {sortButton('serialNumber')}
                                    </TableCell>}
                                {columnNames.includes('name') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <TextField name="name" label="Név" size="small"
                                                   value={searchParams.name || ""} onChange={onSearchParamsChange}
                                                   sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>
                                        {sortButton('name')}
                                    </TableCell>}
                                {columnNames.includes('projectName') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <Autocomplete
                                            value={searchParams.project}
                                            margin="normal"
                                            label="Projekt"
                                            name="project"
                                            onChange={(event, newValue) => onChangeSelect(event, newValue, "project")}
                                            options={projects}
                                            getOptionLabel={(project) => project.name}
                                            sx={{margin: "0px", width: "calc(100% - 40px)", display: "inline-flex"}}
                                            size="small"
                                            renderInput={(params) => (
                                                <TextField {...params} label="Projekt"/>
                                            )}
                                        />
                                        {sortButton('projectName')}
                                    </TableCell>}
                                {columnNames.includes('vehicleLicensePlate') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <Autocomplete
                                            value={searchParams.vehicle}
                                            margin="normal"
                                            label="Jármű"
                                            name="vehicle"
                                            size="small"
                                            onChange={(event, newValue) => onChangeSelect(event, newValue, "vehicle")}
                                            options={vehicles}
                                            getOptionLabel={(vehicle) => vehicle.licensePlate}
                                            sx={{margin: "0px", width: "calc(100% - 40px)", display: "inline-flex"}}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Jármű"/>
                                            )}
                                        />
                                        {sortButton('vehicleLicensePlate')}
                                    </TableCell>}
                                {columnNames.includes('paid') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <FormControl sx={{m: 1, minWidth: 120, margin: "0px", marginLeft: "0px"}} fullWidth size="small">
                                            <InputLabel id="status-label">Státusz</InputLabel>
                                            <Select
                                                variant="outlined"
                                                labelId="status-label"
                                                name="paid"
                                                label="Státusz"
                                                value={(searchParams && searchParams.paid) || ''}
                                                onChange={onSearchParamsChange}
                                                sx={{textAlign: "left"}}
                                            >
                                                <MenuItem value={null}>Összes</MenuItem>
                                                <MenuItem value={'true'}>Teljesített</MenuItem>
                                                <MenuItem value={'false'}>Teljesítetlen</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>}
                                {columnNames.includes('sender') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <TextField name="sender" label="Feladó" size="small"
                                                   value={searchParams.sender || ""} onChange={onSearchParamsChange}
                                                   sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>
                                        {sortButton('sender')}
                                    </TableCell>}
                                {columnNames.includes('receiver') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <TextField name="receiver" label="Címzett" size="small"
                                                   value={searchParams.receiver || ""} onChange={onSearchParamsChange}
                                                   value={searchParams.receiver || ""} onChange={onSearchParamsChange}
                                                   sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>
                                        {sortButton('receiver')}
                                    </TableCell>}
                                {columnNames.includes('amount') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <TextField name="amount" label="Összeg" size="small"
                                                   value={searchParams.amount || ""} onChange={onSearchParamsChange}
                                                   sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>
                                        {sortButton('amount')}
                                    </TableCell>}
                                {columnNames.includes('dateOfInvoice') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        Keltezés
                                        {sortButton('dateOfInvoice')}
                                    </TableCell>}
                                {columnNames.includes('deadline') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        Határidő
                                        {sortButton('deadline')}
                                    </TableCell>}
                                {columnNames.includes('transactionType') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <FormControl sx={{m: 1, minWidth: 120, margin: "0px", marginLeft: "0px"}} fullWidth size="small">
                                            <InputLabel id="transaction-type-label">Tranzakció Típusa</InputLabel>
                                            <Select
                                                variant="outlined"
                                                labelId="transaction-type-label"
                                                name="transactionType"
                                                label="Tranzakció Típusa"
                                                value={(searchParams && searchParams.transactionType) || ""}
                                                onChange={onSearchParamsChange}
                                                sx={{textAlign: "left"}}
                                            >
                                                <MenuItem value={null}>Összes</MenuItem>
                                                {transactionTypes.map((transactionType, index) =>
                                                    <MenuItem key={index}
                                                              value={transactionType.id}>{transactionType.name}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </TableCell>}
                                {columnNames.includes('category') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <FormControl sx={{m: 1, minWidth: 120, margin: "0px", marginLeft: "0px"}} fullWidth size="small">
                                            <InputLabel id="category-label">Kategória</InputLabel>
                                            <Select
                                                variant="outlined"
                                                labelId="category-label"
                                                name="category"
                                                label="Kategória"
                                                value={(searchParams && searchParams.category) || ""}
                                                onChange={onSearchParamsChange}
                                                sx={{textAlign: "left"}}
                                            >
                                                <MenuItem value={null}>Összes</MenuItem>
                                                {categories.map((category, index) =>
                                                    <MenuItem key={index} value={category.id}>{category.name}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </TableCell>}
                                {columnNames.includes('paymentType') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <FormControl sx={{m: 1, minWidth: 120, margin: "0px", marginLeft: "0px"}} fullWidth size="small">
                                            <InputLabel id="payment-type-label">Fizetési mód</InputLabel>
                                            <Select
                                                variant="outlined"
                                                labelId="payment-type-label"
                                                name="paymentType"
                                                label="Fizetési mód"
                                                value={(searchParams && searchParams.paymentType) || ""}
                                                onChange={onSearchParamsChange}
                                                sx={{textAlign: "left"}}
                                            >
                                                <MenuItem value={null}>Összes</MenuItem>
                                                <MenuItem value={"CASH"}>Készpénz</MenuItem>
                                                <MenuItem value={"TRANSFER"}>Átutalás</MenuItem>
                                                <MenuItem value={"CREDIT_CARD"}>Bankkártya</MenuItem>
                                                <MenuItem value={"CASH_ON_DELIVERY"}>Utánvét</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>}
                                {columnNames.includes('invoiceType') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        <FormControl sx={{m: 1, minWidth: 120, margin: "0px", marginLeft: "0px"}} fullWidth size="small">
                                            <InputLabel id="invoice-type-label">Számla típusa</InputLabel>
                                            <Select
                                                variant="outlined"
                                                labelId="invoice-type-label"
                                                name="invoiceType"
                                                label="Számla típusa"
                                                value={(searchParams && searchParams.invoiceType) || ""}
                                                onChange={onSearchParamsChange}
                                                sx={{textAlign: "left"}}
                                            >
                                                <MenuItem value={null}>Összes</MenuItem>
                                                <MenuItem value={"INVOICE"}>Számla</MenuItem>
                                                <MenuItem value={"ADVANCE_INVOICE"}>Előlegszámla</MenuItem>
                                                <MenuItem value={"FEE_REQUEST"}>Díjbekérő</MenuItem>
                                                <MenuItem value={"RECEIPT"}>Nyugta</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>}
                                {columnNames.includes('paidByUserName') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        Fizette
                                    </TableCell>}
                                {columnNames.includes('paidAt') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        Teljesítés Dátuma
                                        {sortButton('paidAt')}
                                    </TableCell>}
                                {columnNames.includes('documentNames') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        Dokumentumok
                                    </TableCell>}
                                {columnNames.includes('createdAt') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        Létrehozva
                                        {sortButton('createdAt')}
                                    </TableCell>}
                                {columnNames.includes('updatedAt') &&
                                    <TableCell sx={{padding: "8px"}}>
                                        Módosítva
                                        {sortButton('updatedAt')}
                                    </TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.rows == null ? <TableRow sx={{textAlign: "center"}}><TableCell colSpan={16} sx={{
                                    padding: "200px",
                                    textAlign: "center"
                                }}><CircularProgress/></TableCell></TableRow> :
                                tableData.rows?.map((row, index) => (
                                    <TableRow key={index} hover
                                              sx={{...getRowBackgroundColor(row)}}>
                                        <TableCell padding='none' align='right' sx={{
                                            display: "inline-flex",
                                            padding: "2px",
                                            width: "100%",
                                            height: "30px !important",
                                            justifyContent: "space-between"
                                        }}>
                                            <Tooltip title={row.paid ? 'Teljesítés visszavonása' : 'Számla teljesítése'} placement='left'>
                                                {row.paid ?
                                                    <MoneyOff onClick={() => togglePaid(row.id, row.paid)} sx={{
                                                        position: "relative",
                                                        top: "3px",
                                                        left: "0px",
                                                        width: "24px",
                                                        color: "#808080",
                                                        cursor: "pointer",
                                                        marginRight: "40px"
                                                    }}/> :
                                                    <AttachMoney onClick={() => handleClickOpen(row)} sx={{
                                                        position: "relative",
                                                        top: "3px",
                                                        left: "0px",
                                                        width: "24px",
                                                        color: "#808080",
                                                        cursor: "pointer",
                                                        marginRight: "40px"
                                                    }}/>}
                                            </Tooltip>
                                            <Tooltip title={"Törlés"} placement="left">
                                                <Delete onClick={() => openConfirmationDialog(
                                                    'Biztosan törölni szeretnéd az alábbi számlát?',
                                                    <>{row.name?.length > 50 ? row.name?.slice(0, 50) + '...' : row.name}<br/>{parseToMoney(row.amount)}<br/>{row.deadline}</>,
                                                    () => deleteSimpleInvoice(row.id)
                                                )} sx={{
                                                    position: "relative", top: "3px", left: "0px", width: "24px", color: "#808080", cursor: "pointer"
                                                }}/>
                                            </Tooltip>
                                        </TableCell>
                                        {ALL_COLUMNS.filter(c => columnNames.includes(c.id)).map((column, index) => (
                                            <TableCell onClick={() => navigate(`/dashboard/simple-invoices/${row.id}`)}
                                                       sx={{
                                                           cursor: "pointer",
                                                           padding: "0px 5px",
                                                           whiteSpace: "nowrap",
                                                           textOverflow: "ellipsis",
                                                           height: "30px !important",
                                                           overflow: "hidden",
                                                           textAlign: column.align
                                                       }}
                                                       key={index}>{column.id === 'documentNames' ? renderDocuments(column, row[column.id], row) : column.fun ? column.fun(row[column.id], row) : row[column.id]}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 250]}
                    component="div"
                    count={tableData.totalCount}
                    rowsPerPage={parseInt(tableData.rowsPerPage)}
                    page={tableData.page}
                    onPageChange={(event, newPage) => setTableData((prev) => ({...prev, page: newPage}))}
                    onRowsPerPageChange={(event) => {
                        setTableData((prev) => ({...prev, rowsPerPage: event.target.value, page: 0}))
                        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, event.target.value)
                    }}
                    labelRowsPerPage="Elemek oldalanként:"
                />
            </Paper>

            <Dialog
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        component: 'form',
                        onSubmit: (event) => {
                            event.preventDefault();
                            const formData = new FormData(event.currentTarget);
                            if (selectedUser) {
                                formData.set('userId', selectedUser.id);
                            }
                            const formJson = Object.fromEntries((formData).entries());

                            if (formJson.amount !== currentItem.amount || currentItem?.totalPaymentParts > 0) saveAsPaymentPart(currentItem.id, formJson)
                            else togglePaid(currentItem.id, currentItem.paid, formJson.paidAt, formJson.userId);
                            handleClose();
                        },
                    }
                }}
            >
                <DialogTitle sx={{background: "#1976d2", color: "white", width: "500px"}}>Számla
                    Teljesítése</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        autoFocus
                        required
                        margin="dense"
                        name="user"
                        onChange={(event, newValue) => setSelectedUser(newValue)}
                        defaultValue={selectedUser}
                        options={users}
                        getOptionLabel={(user) => user.name}
                        sx={{marginTop: "10px"}}
                        renderInput={(params) => (
                            <TextField {...params} label="Teljesítette" variant="standard" required/>
                        )}
                    />
                    <TextField
                        required
                        margin="dense"
                        name="paidAt"
                        label="Teljesítés Dátuma"
                        fullWidth
                        defaultValue={TODAY}
                        variant="standard"
                        type='date'
                        slotProps={{inputLabel: {shrink: true}}}
                        sx={{marginTop: "30px"}}
                    />
                    <TextField
                        required
                        margin="dense"
                        name="amount"
                        label="Összeg (Ft)"
                        fullWidth
                        defaultValue={currentItem?.amount - (currentItem?.totalPaymentParts)}
                        variant="standard"
                        type='number'
                        sx={{marginTop: "30px"}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button type="submit" color="info" variant="contained" sx={{marginRight: "auto"}}>Mentés</Button>
                    <Button onClick={handleClose} color="error" variant="outlined">Mégse</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

function getRowBackgroundColor(row) {
    if (row.sender !== 'Fay Automatika Kft.' && row.receiver !== 'Fay Automatika Kft.') return 'white'
    const isExpense = row.transactionType === 'Kiadás';
    const isExpired = (new Date(row.deadline) < new Date(TODAY) && row.paid !== true);
    const color = isExpired ? '#f5f5c2' : (isExpense ? '#f5d6d6' : '#d5f5d5');

    return {
        ':hover': {
            backgroundColor: isExpired ? '#ececb5' : (isExpense ? '#eccccc' : '#c8ecc8'),
        },
        backgroundColor: color
    };
}

